/**
 * Sprint 2.7.7 Step 5 — Content Brief approval modal.
 *
 * Uses ApprovalModal as the chrome. Supports full brief lifecycle editing
 * and status transitions via the content briefs API.
 */

import { useState, useEffect, useCallback } from 'react';
import type { ContentBrief, ContentBriefStatus } from '../types';
import { ApprovalModal } from './ApprovalModal';
import { api } from '../api';

// ── Status display helpers ────────────────────────────────────────────────────

const STATUS_LABELS: Record<ContentBriefStatus, string> = {
  drafted: 'Drafted',
  approved: 'Approved',
  article_being_written: 'Writing',
  article_published: 'Published',
  discarded: 'Discarded',
};

const STATUS_COLORS: Record<ContentBriefStatus, string> = {
  drafted: 'bg-zinc-100 text-zinc-700',
  approved: 'bg-green-100 text-green-700',
  article_being_written: 'bg-blue-100 text-blue-700',
  article_published: 'bg-indigo-100 text-indigo-700',
  discarded: 'bg-red-100 text-red-600',
};

const EDITABLE_STATUSES: ContentBriefStatus[] = ['drafted', 'article_being_written'];

// ── Form state ────────────────────────────────────────────────────────────────

interface FormState {
  suggestedTitle: string;
  metaDescription: string;
  headings: string; // one per line
  keywordVariants: string; // one per line
  semanticTerms: string; // one per line
  referenceUrls: string; // one per line
  internalLinks: string; // one per line
}

function briefToForm(brief: ContentBrief): FormState {
  return {
    suggestedTitle: brief.suggestedTitle ?? '',
    metaDescription: brief.metaDescription ?? '',
    headings: brief.headings.join('\n'),
    keywordVariants: brief.keywordVariants.join('\n'),
    semanticTerms: brief.semanticTerms.join('\n'),
    referenceUrls: brief.referenceUrls.join('\n'),
    internalLinks: brief.internalLinks.join('\n'),
  };
}

function parseLines(raw: string): string[] {
  return raw
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

function isDirty(form: FormState, brief: ContentBrief): boolean {
  return (
    (form.suggestedTitle || null) !== brief.suggestedTitle ||
    (form.metaDescription || null) !== brief.metaDescription ||
    parseLines(form.headings).join('|') !== brief.headings.join('|') ||
    parseLines(form.keywordVariants).join('|') !== brief.keywordVariants.join('|') ||
    parseLines(form.semanticTerms).join('|') !== brief.semanticTerms.join('|') ||
    parseLines(form.referenceUrls).join('|') !== brief.referenceUrls.join('|') ||
    parseLines(form.internalLinks).join('|') !== brief.internalLinks.join('|')
  );
}

// ── Component props ───────────────────────────────────────────────────────────

export interface ContentBriefModalProps {
  brief: ContentBrief | null;
  isOpen: boolean;
  onClose: () => void;
  onBriefUpdated: (brief: ContentBrief) => void;
  onToast?: (msg: string, kind?: 'success' | 'error') => void;
}

// ── Main component ────────────────────────────────────────────────────────────

export function ContentBriefModal({
  brief,
  isOpen,
  onClose,
  onBriefUpdated,
  onToast,
}: ContentBriefModalProps) {
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [publishUrlInput, setPublishUrlInput] = useState('');
  const [showPublishPrompt, setShowPublishPrompt] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-init form when brief changes
  useEffect(() => {
    if (brief) {
      setForm(briefToForm(brief));
      setPublishUrlInput(brief.publishedUrl ?? '');
      setShowPublishPrompt(false);
      setError(null);
    }
  }, [brief?.id]);

  const canEdit = brief ? EDITABLE_STATUSES.includes(brief.status) : false;

  const setField = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }, []);

  const dirty = brief && form ? isDirty(form, brief) : false;

  // ── Save (PATCH content) ──────────────────────────────────────────────────

  async function handleSave() {
    if (!brief || !form || !dirty) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await api.contentBriefs.updateContent(brief.id, {
        ...(form.suggestedTitle ? { suggestedTitle: form.suggestedTitle } : {}),
        ...(form.metaDescription ? { metaDescription: form.metaDescription } : {}),
        headings: parseLines(form.headings),
        keywordVariants: parseLines(form.keywordVariants),
        semanticTerms: parseLines(form.semanticTerms),
        referenceUrls: parseLines(form.referenceUrls),
        internalLinks: parseLines(form.internalLinks),
      });
      onBriefUpdated(updated);
      onToast?.('Changes saved', 'success');
    } catch {
      setError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  }

  // ── Status transition ─────────────────────────────────────────────────────

  async function handleTransition(toStatus: ContentBriefStatus, publishedUrl?: string) {
    if (!brief) return;
    setTransitioning(true);
    setError(null);
    try {
      const updated = await api.contentBriefs.transition(brief.id, toStatus, publishedUrl);
      onBriefUpdated(updated);
      setShowPublishPrompt(false);
      onToast?.(`Brief marked as ${STATUS_LABELS[toStatus]}`, 'success');
    } catch {
      setError('Status transition failed');
    } finally {
      setTransitioning(false);
    }
  }

  // ── Export ────────────────────────────────────────────────────────────────

  async function handleExport() {
    if (!brief) return;
    setExporting(true);
    setError(null);
    try {
      const blob = await api.contentBriefs.exportMarkdown(brief.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `brief-${brief.id}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      onToast?.('Brief exported', 'success');
    } catch {
      setError('Export failed');
    } finally {
      setExporting(false);
    }
  }

  if (!brief || !form) return null;

  const statusLabel = STATUS_LABELS[brief.status];
  const statusColor = STATUS_COLORS[brief.status];
  const isDiscarded = brief.status === 'discarded';
  const isPublished = brief.status === 'article_published';

  // ── Title slot ────────────────────────────────────────────────────────────

  const titleSlot = (
    <div className="flex items-center gap-2 min-w-0">
      <span className="font-semibold text-gray-900 truncate text-sm">
        {brief.suggestedTitle ?? brief.targetKeyword}
      </span>
      <span className={`shrink-0 inline-flex text-[11px] font-medium px-2 py-0.5 rounded-full ${statusColor}`}>
        {statusLabel}
      </span>
    </div>
  );

  // ── Meta sidebar ──────────────────────────────────────────────────────────

  const metaSlot = (
    <dl className="text-xs space-y-2 text-gray-600">
      <div>
        <dt className="font-medium text-gray-400 uppercase tracking-wide text-[10px]">Keyword</dt>
        <dd className="mt-0.5 font-mono text-gray-800 break-all">{brief.targetKeyword}</dd>
      </div>
      <div>
        <dt className="font-medium text-gray-400 uppercase tracking-wide text-[10px]">Generated</dt>
        <dd className="mt-0.5">{new Date(brief.generatedAt).toLocaleDateString()}</dd>
      </div>
      {brief.approvedAt && (
        <div>
          <dt className="font-medium text-gray-400 uppercase tracking-wide text-[10px]">Approved</dt>
          <dd className="mt-0.5">{new Date(brief.approvedAt).toLocaleDateString()}</dd>
        </div>
      )}
      {brief.articlePublishedAt && (
        <div>
          <dt className="font-medium text-gray-400 uppercase tracking-wide text-[10px]">Published</dt>
          <dd className="mt-0.5">{new Date(brief.articlePublishedAt).toLocaleDateString()}</dd>
        </div>
      )}
      {brief.publishedUrl && (
        <div>
          <dt className="font-medium text-gray-400 uppercase tracking-wide text-[10px]">Published URL</dt>
          <dd className="mt-0.5 break-all">
            <a
              href={brief.publishedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline hover:text-blue-800"
            >
              {brief.publishedUrl}
            </a>
          </dd>
        </div>
      )}
      {brief.exportedAt && (
        <div>
          <dt className="font-medium text-gray-400 uppercase tracking-wide text-[10px]">Exported</dt>
          <dd className="mt-0.5">{new Date(brief.exportedAt).toLocaleDateString()}</dd>
        </div>
      )}
      {brief.tokensIn !== null && (
        <div>
          <dt className="font-medium text-gray-400 uppercase tracking-wide text-[10px]">Tokens</dt>
          <dd className="mt-0.5">{(brief.tokensIn ?? 0) + (brief.tokensOut ?? 0)} total</dd>
        </div>
      )}
    </dl>
  );

  // ── Footer actions ────────────────────────────────────────────────────────

  const actionsSlot = (
    <div className="flex flex-wrap items-center gap-2">
      {/* Export — available for all non-discarded statuses */}
      {!isDiscarded && (
        <button
          onClick={handleExport}
          disabled={exporting}
          className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          {exporting ? 'Exporting…' : 'Export .md'}
        </button>
      )}

      {/* Save — only visible in editable statuses, only enabled when dirty */}
      {canEdit && (
        <button
          onClick={handleSave}
          disabled={!dirty || saving}
          className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-40 transition-colors"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      )}

      {/* Status-specific primary actions */}
      {brief.status === 'drafted' && (
        <>
          <button
            onClick={() => handleTransition('approved')}
            disabled={transitioning}
            className="px-4 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            Approve
          </button>
          <button
            onClick={() => handleTransition('discarded')}
            disabled={transitioning}
            className="px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
          >
            Discard
          </button>
        </>
      )}

      {brief.status === 'approved' && (
        <>
          <button
            onClick={() => handleTransition('article_being_written')}
            disabled={transitioning}
            className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            Start Writing
          </button>
          <button
            onClick={() => handleTransition('drafted')}
            disabled={transitioning}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Revert to Draft
          </button>
          <button
            onClick={() => handleTransition('discarded')}
            disabled={transitioning}
            className="px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
          >
            Discard
          </button>
        </>
      )}

      {brief.status === 'article_being_written' && (
        <>
          {showPublishPrompt ? (
            <div className="flex items-center gap-2">
              <input
                type="url"
                value={publishUrlInput}
                onChange={(e) => setPublishUrlInput(e.target.value)}
                placeholder="https://example.com/article"
                className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 w-60 focus:outline-none focus:ring-1 focus:ring-indigo-300"
              />
              <button
                onClick={() => handleTransition('article_published', publishUrlInput || undefined)}
                disabled={transitioning}
                className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {transitioning ? 'Saving…' : 'Confirm'}
              </button>
              <button
                onClick={() => setShowPublishPrompt(false)}
                className="px-2 py-1.5 text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowPublishPrompt(true)}
              className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Mark as Published
            </button>
          )}
          <button
            onClick={() => handleTransition('approved')}
            disabled={transitioning}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Revert to Approved
          </button>
          <button
            onClick={() => handleTransition('discarded')}
            disabled={transitioning}
            className="px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
          >
            Discard
          </button>
        </>
      )}

      {isPublished && (
        <button
          onClick={() => handleTransition('article_being_written')}
          disabled={transitioning}
          className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          Revert to Writing
        </button>
      )}
    </div>
  );

  // ── Body ──────────────────────────────────────────────────────────────────

  return (
    <ApprovalModal
      title={titleSlot}
      isOpen={isOpen}
      onClose={onClose}
      actions={actionsSlot}
      meta={metaSlot}
    >
      {error && (
        <div className="mb-4 px-3 py-2 bg-red-50 text-red-700 text-sm rounded-lg border border-red-100">
          {error}
        </div>
      )}

      <div className="space-y-5">
        {/* Suggested title */}
        <FieldGroup label="Suggested title" htmlFor="cb-title">
          <input
            id="cb-title"
            type="text"
            value={form.suggestedTitle}
            onChange={(e) => setField('suggestedTitle', e.target.value)}
            disabled={!canEdit}
            maxLength={200}
            placeholder="Article title…"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-300 disabled:bg-gray-50 disabled:text-gray-500"
          />
        </FieldGroup>

        {/* Meta description */}
        <FieldGroup label="Meta description" htmlFor="cb-meta">
          <textarea
            id="cb-meta"
            rows={2}
            value={form.metaDescription}
            onChange={(e) => setField('metaDescription', e.target.value)}
            disabled={!canEdit}
            maxLength={300}
            placeholder="160-character meta description…"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-300 disabled:bg-gray-50 disabled:text-gray-500 resize-none"
          />
        </FieldGroup>

        {/* Headings */}
        <ArrayField
          label="Suggested headings"
          htmlFor="cb-headings"
          hint="One heading per line"
          value={form.headings}
          onChange={(v) => setField('headings', v)}
          disabled={!canEdit}
          rows={6}
          placeholder="H2: Introduction&#10;H2: Main topic&#10;H3: Sub-topic"
        />

        {/* Keyword variants */}
        <ArrayField
          label="Keyword variants"
          htmlFor="cb-kw-variants"
          hint="One variant per line"
          value={form.keywordVariants}
          onChange={(v) => setField('keywordVariants', v)}
          disabled={!canEdit}
          rows={4}
          placeholder="best seo tools&#10;top seo software"
        />

        {/* Semantic terms */}
        <ArrayField
          label="Semantic terms"
          htmlFor="cb-semantic"
          hint="One term per line"
          value={form.semanticTerms}
          onChange={(v) => setField('semanticTerms', v)}
          disabled={!canEdit}
          rows={4}
          placeholder="organic traffic&#10;search engine optimisation"
        />

        {/* Reference URLs */}
        <ArrayField
          label="Reference URLs"
          htmlFor="cb-refs"
          hint="One URL per line"
          value={form.referenceUrls}
          onChange={(v) => setField('referenceUrls', v)}
          disabled={!canEdit}
          rows={3}
          placeholder="https://competitor.com/article"
        />

        {/* Internal links */}
        <ArrayField
          label="Internal link suggestions"
          htmlFor="cb-internal"
          hint="One per line (slug or full path)"
          value={form.internalLinks}
          onChange={(v) => setField('internalLinks', v)}
          disabled={!canEdit}
          rows={3}
          placeholder="/blog/related-article"
        />
      </div>
    </ApprovalModal>
  );
}

// ── Small reusable sub-components ─────────────────────────────────────────────

function FieldGroup({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-xs font-medium text-gray-500 mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}

function ArrayField({
  label,
  htmlFor,
  hint,
  value,
  onChange,
  disabled,
  rows,
  placeholder,
}: {
  label: string;
  htmlFor: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  rows: number;
  placeholder?: string;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <label htmlFor={htmlFor} className="text-xs font-medium text-gray-500">
          {label}
        </label>
        <span className="text-[10px] text-gray-400">{hint}</span>
      </div>
      <textarea
        id={htmlFor}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        className="w-full text-sm font-mono border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-300 disabled:bg-gray-50 disabled:text-gray-500 resize-none"
      />
    </div>
  );
}
